import React, { Component } from "react";
import { withStyles } from "@mui/styles";
import { withTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import InfiniteScroll from "react-infinite-scroll-component";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Grid from "@mui/material/Grid";
import { toast } from "react-toastify";
import BinMappingGeneration from "./BinMappingGeneration";
import LOCATION_TYPE from "../enum/location_type";
import { generateLocationCodesPDF } from "./pdfGenerator";

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  searchContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    alignItems: "flex-end",
    flexWrap: "wrap",
  },
  searchField: {
    flex: 1,
    maxWidth: "400px",
  },
});

class BinMapping extends Component {
  constructor(props) {
    super(props);
    this.state = {
      locationCodes: [],
      loading: false,
      generatingPDF: false,
      deleting: false,
      search: "",
      page: 1,
      pageSize: 20,
      hasMore: true,
      totalRecords: 0,
      anchorEl: null,
      selectedLocation: null,
      deleteDialogOpen: false,
      addLocationDialogOpen: false,
      boxes: [],
      selectedBoxId: null,
      loadingBoxes: false,
      accounts: [],
      accountValues: [],
      selectedAccountId: null,
      loadingAccounts: false,
      editDialogOpen: false,
      editForm: {
        id: null,
        location: "",
        location_code: "",
        color_code: "#f97316",
      },
      savingEdit: false,
      generatePDFDialogOpen: false,
      pdfRangeForm: {
        letterRangeStart: "",
        letterRangeEnd: "",
        numberRangeStart: "",
        numberRangeEnd: "",
      },
      generatingPDFFromRange: false,
    };
    this.searchTimeout = null;
  }

  componentDidMount() {
    this.fetchAccounts();
  }

  fetchAccounts = async () => {
    this.setState({ loadingAccounts: true });
    try {
      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      
      const response = await fetch(
        process.env.REACT_APP_BASEURL + "accounts",
        {
          method: "GET",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const accounts = await response.json();
      const accountValues = Array.isArray(accounts) ? accounts.map((account) => {
        return { value: account.id, label: account.name };
      }) : [];
      
      this.setState({ 
        accounts: accounts || [],
        accountValues: accountValues,
        loadingAccounts: false 
      }, () => {
        if (accountValues.length > 0 && accountValues[0].value) {
          this.setState({ selectedAccountId: accountValues[0].value }, () => {
            this.fetchBoxes();
          });
        }
      });
    } catch (error) {
      console.error("Error fetching accounts:", error);
      this.setState({ loadingAccounts: false });
      toast.error(this.props.t("BinMapping.ErrorFetchingAccounts") || "Error fetching accounts");
    }
  };

  fetchBoxes = async () => {
    if (!this.state.selectedAccountId) {
      this.setState({ boxes: [], loadingBoxes: false });
      return;
    }

    this.setState({ loadingBoxes: true });
    try {
      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      
      const BoxesObj = {
        store_ids: [this.state.selectedAccountId]
      };
      
      const response = await fetch(
        process.env.REACT_APP_BASEURL + "getBoxesByStoreIds",
        {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(BoxesObj),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        throw new Error(`Failed to parse JSON response: ${jsonError.message}`);
      }
      
      const allBoxes = Array.isArray(data) ? data : [];
      
      const filteredBoxes = allBoxes.filter(box => {
        const locationTypeId = box.location_type_id || (box.type && box.type.id);
        return locationTypeId === LOCATION_TYPE.Counter;
      });
      
      this.setState({ 
        boxes: filteredBoxes,
        loadingBoxes: false,
        selectedBoxId: null,
        locationCodes: [],
      }, () => {
        if (filteredBoxes.length > 0 && filteredBoxes[0].id) {
          const firstBoxId = String(filteredBoxes[0].id);
          this.setState({ selectedBoxId: firstBoxId }, () => {
            this.fetchLocationCodes(true);
          });
        }
      });
    } catch (error) {
      console.error("Error fetching boxes:", error);
      this.setState({ loadingBoxes: false });
      toast.error(this.props.t("BinMapping.ErrorFetchingBoxes") || "Error fetching boxes");
    }
  };

  fetchLocationCodes = async (reset = false) => {
    if (!this.state.selectedBoxId) {
      this.setState({ loading: false });
      return;
    }

    if (reset) {
      this.setState({ loading: true, page: 1, locationCodes: [], hasMore: true });
    } else {
      this.setState({ loading: true });
    }

    try {
      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      
      const requestBody = {
        box_id: this.state.selectedBoxId || null,
        page: reset ? 1 : this.state.page,
        pageSize: this.state.pageSize,
      };

      if (this.state.search && this.state.search.trim() !== "") {
        requestBody.search = this.state.search.trim();
      }

      const response = await fetch(
        process.env.REACT_APP_BASEURL + "deliver/locations",
        {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      const data = result.data || result;
      const locations = data.locations || [];
      
      if (locations && Array.isArray(locations) && locations.length >= 0) {
        const totalLocations = data.total_locations || (data.pagination && data.pagination.total) || locations.length;
        const currentPage = reset ? 1 : this.state.page;
        const newLocations = reset ? locations : [...this.state.locationCodes, ...locations];
        
        const pagination = data.pagination || {};
        const hasMoreData = pagination.totalPages ? currentPage < pagination.totalPages : newLocations.length < totalLocations;
        
        this.setState({
          locationCodes: newLocations,
          totalRecords: totalLocations,
          hasMore: hasMoreData,
          page: currentPage + 1,
          loading: false,
        });
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error("Error fetching location codes:", error);
      this.setState({ loading: false });
    }
  };

  loadMore = () => {
    if (!this.state.loading && this.state.hasMore) {
      this.fetchLocationCodes(false);
    }
  };

  handleSearchChange = (event) => {
    const value = event.target.value;
    this.setState({ search: value, page: 1, locationCodes: [], hasMore: true }, () => {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.fetchLocationCodes(true);
      }, 500);
    });
  };

  handleAccountChange = (event) => {
    const accountId = event.target.value;
    this.setState({ 
      selectedAccountId: accountId || null,
      selectedBoxId: null,
      search: "",
      page: 1,
      locationCodes: [],
      hasMore: true
    }, () => {
      if (accountId) {
        this.fetchBoxes();
      }
    });
  };

  handleBoxChange = (event) => {
    const boxId = event.target.value;
    const boxIdString = boxId ? String(boxId) : null;
    
    this.setState({ 
      selectedBoxId: boxIdString, 
      search: "", 
      page: 1, 
      locationCodes: [], 
      hasMore: true 
    }, () => {
      if (boxId) {
        this.fetchLocationCodes(true);
      }
    });
  };

  getContrastColor = (hexColor) => {
    try {
      if (!hexColor || typeof hexColor !== 'string' || !hexColor.startsWith('#')) {
        return '#000000';
      }
      
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);
      
      if (isNaN(r) || isNaN(g) || isNaN(b)) {
        return '#000000';
      }
      
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    } catch (error) {
      return '#000000';
    }
  };

  handleMenuOpen = (event, location) => {
    event.stopPropagation();
    this.setState({
      anchorEl: event.currentTarget,
      selectedLocation: location,
    });
  };

  handleMenuClose = () => {
    this.setState({
      anchorEl: null,
      selectedLocation: null,
    });
  };

  handleDelete = () => {
    if (!this.state.selectedLocation) {
      this.handleMenuClose();
      return;
    }
    const locationToDelete = this.state.selectedLocation;
    this.handleMenuClose();
    this.setState({ 
      deleteDialogOpen: true,
      selectedLocation: locationToDelete 
    });
  };

  handleDeleteDialogClose = () => {
    this.setState({ deleteDialogOpen: false, selectedLocation: null });
  };

  handleEditOpen = () => {
    if (!this.state.selectedLocation) {
      this.handleMenuClose();
      return;
    }
    const { selectedLocation } = this.state;
    this.setState({
      editDialogOpen: true,
      editForm: {
        id: selectedLocation.id,
        location: selectedLocation.location || "",
        location_code: selectedLocation.location_code || "",
        color_code: selectedLocation.color_code || "#f97316",
      },
    });
    this.handleMenuClose();
  };

  handleEditClose = () => {
    this.setState({
      editDialogOpen: false,
      editForm: {
        id: null,
        location: "",
        location_code: "",
        color_code: "#f97316",
      },
      savingEdit: false,
    });
  };

  handleEditFieldChange = (field) => (event) => {
    const value = event.target.value;
    this.setState((prev) => ({
      editForm: { ...prev.editForm, [field]: value },
    }));
  };

  handleEditColorSelect = (color) => {
    this.setState((prev) => ({
      editForm: { ...prev.editForm, color_code: color },
    }));
  };

  handleEditSave = async () => {
    const { editForm, selectedBoxId } = this.state;
    if (!editForm.id || !editForm.location || !editForm.location_code) {
      toast.error(this.props.t("Location.CodeGeneration.CodeRequired") || "All fields are required");
      return;
    }
    this.setState({ savingEdit: true });
    try {
      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      const payload = {
        id: editForm.id,
        box_id: selectedBoxId,
        location: editForm.location,
        location_code: editForm.location_code,
        color_code: editForm.color_code,
      };

      const response = await fetch(
        process.env.REACT_APP_BASEURL + "deliver/locations/update",
        {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      if (result && result.success) {
        toast.success(result.message || "Location updated");
        this.handleEditClose();
        this.fetchLocationCodes(true);
      } else {
        throw new Error(result && result.message ? result.message : "Failed to update location");
      }
    } catch (error) {
      console.error("Error updating location:", error);
      toast.error(error.message || "Failed to update location");
      this.setState({ savingEdit: false });
    }
  };

  handleConfirmDelete = async () => {
    if (!this.state.selectedLocation || !this.state.selectedLocation.id) {
      toast.error(this.props.t("Location.CodeGeneration.DeleteError"));
      this.handleDeleteDialogClose();
      return;
    }

    const locationId = this.state.selectedLocation.id;
    this.setState({ deleting: true });

    try {
      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      
      const response = await fetch(
        process.env.REACT_APP_BASEURL + "deliver/locations/delete",
        {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify({
            location_id: locationId,
          }),
        }
      );

      let result;
      try {
        result = await response.json();
      } catch (jsonError) {
        const textError = await response.text();
        throw new Error(textError || `HTTP error! status: ${response.status}`);
      }

      if (result && result.error) {
        const errorMessage = result.error.message || result.error.code || this.props.t("Location.CodeGeneration.DeleteError");
        throw new Error(errorMessage);
      }

      if (result && result.success === true) {
        toast.success(
          result.message || this.props.t("Location.CodeGeneration.DeleteSuccess")
        );
        
        const updatedLocations = this.state.locationCodes.filter(
          (loc) => loc.id !== locationId
        );
        
        this.setState({
          locationCodes: updatedLocations,
          totalRecords: Math.max(0, this.state.totalRecords - 1),
          deleting: false,
          deleteDialogOpen: false,
          selectedLocation: null,
        });

        this.fetchLocationCodes(true);
      } else {
        const errorMessage = result && result.message 
          ? result.message 
          : this.props.t("Location.CodeGeneration.DeleteError");
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error deleting location:", error);
      this.setState({ deleting: false, deleteDialogOpen: false });
      const errorMessage = error.message || this.props.t("Location.CodeGeneration.DeleteError");
      toast.error(errorMessage);
    }
  };

  handleGeneratePDF = async (forAll = false) => {
    if (!forAll) {
      this.handleMenuClose();
    }
    
    if (!this.state.selectedBoxId) {
      toast.error(this.props.t("Location.CodeGeneration.BoxIdRequired"));
      return;
    }

    this.setState({ generatingPDF: true });

    try {
      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      
      const requestBody = {
        box_id: this.state.selectedBoxId,
      };

      if (!forAll && this.state.selectedLocation) {
        requestBody.location_ids = [this.state.selectedLocation.id];
      }

      if (this.state.search && this.state.search.trim() !== "") {
        requestBody.search = this.state.search.trim();
      }

      const response = await fetch(
        process.env.REACT_APP_BASEURL + "deliver/locations/generate-pdf-data",
        {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorText = await response.text();
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } catch (parseError) {
            errorMessage = errorText || errorMessage;
          }
        } catch (e) {
          console.error("Could not parse error response:", e);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (!data.success || !data.locations || !Array.isArray(data.locations)) {
        throw new Error(data.message || "Invalid response from server");
      }

      await generateLocationCodesPDF(data.locations, this.state.selectedBoxId);

      this.setState({ generatingPDF: false });
      toast.success(
        this.props.t("Location.CodeGeneration.PDFGeneratedSuccess")
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
      this.setState({ generatingPDF: false });
      toast.error(
        this.props.t("Location.CodeGeneration.PDFGenerationError")
      );
    }
  };

  handleAddLocationClick = () => {
    if (!this.state.selectedBoxId) {
      toast.error(this.props.t("Location.CodeGeneration.BoxIdRequired"));
      return;
    }
    this.setState({ addLocationDialogOpen: true });
  };

  handleAddLocationDialogClose = () => {
    this.setState({ addLocationDialogOpen: false });
  };

  handleLocationGenerated = () => {
    this.fetchLocationCodes(true);
    this.handleAddLocationDialogClose();
  };

  handleGeneratePDFDialogOpen = () => {
    if (!this.state.selectedBoxId) {
      toast.error(this.props.t("Location.CodeGeneration.BoxIdRequired"));
      return;
    }
    this.setState({ 
      generatePDFDialogOpen: true,
      pdfRangeForm: {
        letterRangeStart: "",
        letterRangeEnd: "",
        numberRangeStart: "",
        numberRangeEnd: "",
      },
    });
  };

  handleGeneratePDFDialogClose = () => {
    this.setState({ 
      generatePDFDialogOpen: false,
      pdfRangeForm: {
        letterRangeStart: "",
        letterRangeEnd: "",
        numberRangeStart: "",
        numberRangeEnd: "",
      },
      generatingPDFFromRange: false,
    });
  };

  handlePDFRangeFormChange = (field) => (event) => {
    const value = event.target.value;
    this.setState((prev) => ({
      pdfRangeForm: {
        ...prev.pdfRangeForm,
        [field]: value,
      },
    }));
  };

  filterLocationsByRange = (locations, letterRangeStart, letterRangeEnd, numberRangeStart, numberRangeEnd) => {
    const letterStart = letterRangeStart.toUpperCase().charCodeAt(0);
    const letterEnd = letterRangeEnd.toUpperCase().charCodeAt(0);
    const numStart = parseInt(numberRangeStart, 10);
    const numEnd = parseInt(numberRangeEnd, 10);

    return locations.filter((location) => {
      const locationCode = (location.location || "").toUpperCase().trim();
      const match = locationCode.match(/^([A-Z])(\d+)$/);
      
      if (!match) {
        return false;
      }

      const locationLetter = match[1].charCodeAt(0);
      const locationNumber = parseInt(match[2], 10);
      const letterInRange = locationLetter >= letterStart && locationLetter <= letterEnd;
      const numberInRange = locationNumber >= numStart && locationNumber <= numEnd;

      return letterInRange && numberInRange;
    });
  };

  fetchAllLocations = async () => {
    const { selectedBoxId } = this.state;
    
    if (!selectedBoxId) {
      throw new Error(this.props.t("Location.CodeGeneration.BoxIdRequired"));
    }

    try {
      const token = JSON.parse(sessionStorage.getItem("user")).signInUserSession.idToken.jwtToken;
      const requestBody = {
        box_id: selectedBoxId,
      };

      const response = await fetch(
        process.env.REACT_APP_BASEURL + "deliver/locations/generate-pdf-data",
        {
          method: "POST",
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorText = await response.text();
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } catch (parseError) {
            errorMessage = errorText || errorMessage;
          }
        } catch (e) {
          console.error("Could not parse error response:", e);
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      if (!data.success || !data.locations || !Array.isArray(data.locations)) {
        throw new Error(data.message || "Invalid response from server");
      }

      return data.locations;
    } catch (error) {
      console.error("Error fetching locations:", error);
      throw error;
    }
  };

  handleGeneratePDFFromRange = async () => {
    const { pdfRangeForm, selectedBoxId } = this.state;
    const { letterRangeStart, letterRangeEnd, numberRangeStart, numberRangeEnd } = pdfRangeForm;

    if (!selectedBoxId) {
      toast.error(this.props.t("Location.CodeGeneration.BoxIdRequired"));
      return;
    }

    if (!letterRangeStart || !letterRangeEnd || !numberRangeStart || !numberRangeEnd) {
      toast.error(this.props.t("Location.CodeGeneration.RangeRequired"));
      return;
    }

    if (!/^[A-Za-z]$/.test(letterRangeStart) || !/^[A-Za-z]$/.test(letterRangeEnd)) {
      toast.error(this.props.t("Location.CodeGeneration.InvalidRangeChar"));
      return;
    }

    if (letterRangeStart.localeCompare(letterRangeEnd) > 0) {
      toast.error(this.props.t("Location.CodeGeneration.InvalidLetterRange"));
      return;
    }

    const startNum = parseInt(numberRangeStart, 10);
    const endNum = parseInt(numberRangeEnd, 10);
    if (isNaN(startNum) || isNaN(endNum)) {
      toast.error(this.props.t("Location.CodeGeneration.InvalidNumberRange"));
      return;
    }

    if (startNum < 1 || endNum < 1) {
      toast.error(this.props.t("Location.CodeGeneration.NumberRangeOutOfBounds"));
      return;
    }

    if (startNum > endNum) {
      toast.error(this.props.t("Location.CodeGeneration.InvalidNumberRange"));
      return;
    }

    this.setState({ generatingPDFFromRange: true });

    try {
      const allLocations = await this.fetchAllLocations();

      if (allLocations.length === 0) {
        toast.error(this.props.t("Location.CodeGeneration.NoLocationsFound") || "No locations found");
        this.setState({ generatingPDFFromRange: false });
        return;
      }

      const filteredLocations = this.filterLocationsByRange(
        allLocations,
        letterRangeStart,
        letterRangeEnd,
        numberRangeStart,
        numberRangeEnd
      );

      if (filteredLocations.length === 0) {
        toast.error(this.props.t("Location.CodeGeneration.RangeIncorrect") || "Range is incorrect");
        this.setState({ generatingPDFFromRange: false });
        return;
      }

      const letterStart = letterRangeStart.toUpperCase().charCodeAt(0);
      const letterEnd = letterRangeEnd.toUpperCase().charCodeAt(0);
      const numStart = parseInt(numberRangeStart, 10);
      const numEnd = parseInt(numberRangeEnd, 10);

      const expectedLocations = [];
      for (let letterCode = letterStart; letterCode <= letterEnd; letterCode++) {
        for (let num = numStart; num <= numEnd; num++) {
          expectedLocations.push(String.fromCharCode(letterCode) + num);
        }
      }

      const foundLocationCodes = filteredLocations.map(loc => {
        return (loc.location || "").toUpperCase().trim();
      });

      const missingLocations = expectedLocations.filter(expected => !foundLocationCodes.includes(expected));

      if (missingLocations.length > 0) {
        toast.error(this.props.t("Location.CodeGeneration.RangeIncorrect") || "Range is incorrect");
        this.setState({ generatingPDFFromRange: false });
        return;
      }

      await generateLocationCodesPDF(filteredLocations, selectedBoxId);

      this.setState({ generatingPDFFromRange: false });
      toast.success(
        this.props.t("Location.CodeGeneration.PDFGeneratedSuccess", {
          count: filteredLocations.length,
        }) || `PDF generated successfully with ${filteredLocations.length} location(s)`
      );
      
      this.handleGeneratePDFDialogClose();
    } catch (error) {
      console.error("Error generating PDF:", error);
      this.setState({ generatingPDFFromRange: false });
      toast.error(
        error.message || this.props.t("Location.CodeGeneration.PDFGenerationError") || "Error generating PDF"
      );
    }
  };

  handleGeneratePDFForAll = async () => {
    const { selectedBoxId } = this.state;

    if (!selectedBoxId) {
      toast.error(this.props.t("Location.CodeGeneration.BoxIdRequired"));
      return;
    }

    this.setState({ generatingPDFFromRange: true });

    try {
      const allLocations = await this.fetchAllLocations();

      if (allLocations.length === 0) {
        toast.error(this.props.t("Location.CodeGeneration.NoLocationsFound") || "No locations found");
        this.setState({ generatingPDFFromRange: false });
        return;
      }

      await generateLocationCodesPDF(allLocations, selectedBoxId);

      this.setState({ generatingPDFFromRange: false });
      toast.success(
        this.props.t("Location.CodeGeneration.PDFGeneratedSuccess", {
          count: allLocations.length,
        }) || `PDF generated successfully with ${allLocations.length} location(s)`
      );
      
      this.handleGeneratePDFDialogClose();
    } catch (error) {
      console.error("Error generating PDF:", error);
      this.setState({ generatingPDFFromRange: false });
      toast.error(
        error.message || this.props.t("Location.CodeGeneration.PDFGenerationError") || "Error generating PDF"
      );
    }
  };

  render() {
    const { classes, t } = this.props;
    const { 
      locationCodes, 
      loading, 
      generatingPDF, 
      deleting, 
      savingEdit,
      search, 
      hasMore, 
      totalRecords, 
      anchorEl, 
      deleteDialogOpen, 
      selectedLocation,
      addLocationDialogOpen,
      editDialogOpen,
      editForm,
      boxes,
      selectedBoxId,
      loadingBoxes,
      accountValues,
      selectedAccountId
    } = this.state;
    const isMenuOpen = Boolean(anchorEl);
    const selectedBox = boxes.find(box => String(box.id) === String(selectedBoxId));

    const colorOptions = [
      { color: "#f97316", name: "Orange" },
      { color: "#7c3aed", name: "Purple" },
      { color: "#ef4444", name: "Red" },
      { color: "#3b82f6", name: "Blue" },
      { color: "#10b981", name: "Green" },
    ];

    return (
      <div className={classes.root}>
        <Box className={classes.header}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: "#1e293b" }}>
            {t("BinMapping.Title") || "Bin Mapping"}
          </Typography>
        </Box>

        {/* Account, Box Selector and Search Bar */}
        <Box className={classes.searchContainer}>
          <Box sx={{ minWidth: 220 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 0.5, 
                color: "#475569", 
                fontWeight: 500,
                fontSize: "0.875rem"
              }}
            >
              {t("BinMapping.SelectAccount") || "Select Account"}
            </Typography>
            <FormControl 
              variant="outlined" 
              size="small" 
              fullWidth
              sx={{ 
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7c3aed",
                      borderWidth: "2px",
                    },
                  },
                  "&.Mui-focused": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7c3aed",
                      borderWidth: "2px",
                    },
                    boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.1)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#f1f5f9",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e2e8f0",
                    },
                  },
                },
              }}
            >
              <Select
                value={selectedAccountId || ""}
                onChange={this.handleAccountChange}
                displayEmpty
                renderValue={(value) => {
                  if (!value) {
                    if (this.state.loadingAccounts) {
                      return <Typography component="span" sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                        Loading accounts...
                      </Typography>;
                    }
                    return <Typography component="span" sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                      {t("BinMapping.SelectAccount") || "Select Account"}
                    </Typography>;
                  }
                  const selectedAccount = accountValues.find(acc => acc.value === value);
                  return selectedAccount ? selectedAccount.label : "";
                }}
              >
                {this.state.loadingAccounts ? (
                  <MenuItem value="" disabled>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                        Loading accounts...
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : accountValues.length === 0 ? (
                  <MenuItem value="" disabled>
                    <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem", fontStyle: "italic" }}>
                      No accounts available
                    </Typography>
                  </MenuItem>
                ) : (
                  accountValues.map((account) => (
                    <MenuItem key={account.value} value={account.value}>
                      {account.label}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ minWidth: 220 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 0.5, 
                color: "#475569", 
                fontWeight: 500,
                fontSize: "0.875rem"
              }}
            >
              {t("BinMapping.DeliveryLocation") || "Delivery Location"}
            </Typography>
            <FormControl 
              variant="outlined" 
              size="small" 
              fullWidth
              sx={{ 
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7c3aed",
                      borderWidth: "2px",
                    },
                  },
                  "&.Mui-focused": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7c3aed",
                      borderWidth: "2px",
                    },
                    boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.1)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#f1f5f9",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e2e8f0",
                    },
                  },
                },
              }}
            >
              <Select
                value={selectedBoxId || ""}
                onChange={this.handleBoxChange}
                disabled={loadingBoxes || !selectedAccountId || boxes.length === 0}
                displayEmpty
                renderValue={(value) => {
                  if (!value) {
                    if (!selectedAccountId) {
                      return <Typography component="span" sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                        {t("BinMapping.SelectAccountFirst") || "Please select an account first"}
                      </Typography>;
                    }
                    if (boxes.length === 0 && !loadingBoxes) {
                      return <Typography component="span" sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                        {t("BinMapping.NoCounterBoxes") || "No Counter boxes found"}
                      </Typography>;
                    }
                    if (loadingBoxes) {
                      return <Typography component="span" sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                        Loading boxes...
                      </Typography>;
                    }
                  }
                  const selectedBox = boxes.find(box => String(box.id) === String(value));
                  return selectedBox ? selectedBox.name || selectedBox.id : "";
                }}
              >
                {!selectedAccountId ? (
                  <MenuItem value="" disabled>
                    <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem", fontStyle: "italic" }}>
                      {t("BinMapping.SelectAccountFirst") || "Please select an account first"}
                    </Typography>
                  </MenuItem>
                ) : boxes.length === 0 && !loadingBoxes ? (
                  <MenuItem value="" disabled>
                    <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem", fontStyle: "italic" }}>
                      {t("BinMapping.NoCounterBoxes") || "No Counter boxes found"}
                    </Typography>
                  </MenuItem>
                ) : loadingBoxes ? (
                  <MenuItem value="" disabled>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography sx={{ color: "#94a3b8", fontSize: "0.875rem" }}>
                        Loading boxes...
                      </Typography>
                    </Box>
                  </MenuItem>
                ) : (
                  boxes.map((box) => (
                    <MenuItem key={box.id} value={String(box.id)}>
                      {box.name || box.id}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
          
          <Box sx={{ flex: 1, maxWidth: "400px" }}>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 0.5, 
                color: "#475569", 
                fontWeight: 500,
                fontSize: "0.875rem"
              }}
            >
              {t("Location.CodeGeneration.SearchPlaceholder") || "Search"}
            </Typography>
            <TextField
              variant="outlined"
              placeholder={t("Location.CodeGeneration.SearchPlaceholder") || "Search by location or location code"}
              value={search}
              onChange={this.handleSearchChange}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#64748b" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7c3aed",
                      borderWidth: "2px",
                    },
                  },
                  "&.Mui-focused": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#7c3aed",
                      borderWidth: "2px",
                    },
                    boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.1)",
                  },
                },
                "& .MuiInputBase-input": {
                  "&::placeholder": {
                    color: "#94a3b8",
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>
          
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={this.handleAddLocationClick}
              disabled={!selectedBoxId}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                padding: "10px 24px",
                fontSize: "0.875rem",
                fontWeight: 600,
                backgroundColor: "#7c3aed",
                boxShadow: "0 2px 4px rgba(124, 58, 237, 0.2)",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#6d28d9",
                  boxShadow: "0 4px 8px rgba(124, 58, 237, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  backgroundColor: "#cbd5e1",
                  color: "#94a3b8",
                  boxShadow: "none",
                  cursor: "not-allowed",
                },
              }}
            >
              {t("BinMapping.AddLocation") || "Add Location"}
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              onClick={this.handleGeneratePDFDialogOpen}
              disabled={!selectedBoxId}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                padding: "10px 24px",
                fontSize: "0.875rem",
                fontWeight: 600,
                borderColor: "#7c3aed",
                color: "#7c3aed",
                boxShadow: "0 2px 4px rgba(124, 58, 237, 0.1)",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#6d28d9",
                  backgroundColor: "rgba(124, 58, 237, 0.05)",
                  boxShadow: "0 4px 8px rgba(124, 58, 237, 0.2)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
                "&:disabled": {
                  borderColor: "#cbd5e1",
                  color: "#94a3b8",
                  boxShadow: "none",
                  cursor: "not-allowed",
                },
              }}
            >
              {t("Location.CodeGeneration.GeneratePDF") || "Generate PDF"}
            </Button>
          </Box>
        </Box>

        {/* Location Codes List */}
        {loading && locationCodes.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : locationCodes.length === 0 && !loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "300px",
            }}
          >
            <Typography variant="body1" sx={{ color: "#64748b" }}>
              {t("Location.CodeGeneration.NoLocationsFound")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ mb: 3 }}>
            {totalRecords > 0 && (
              <Typography variant="body2" sx={{ mb: 2, color: "#64748b" }}>
                {totalRecords} {t("Location.CodeGeneration.TotalLocations")}
              </Typography>
            )}
            <Table size="small" sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow
                  sx={{
                    background: "#f1f5f9",
                    "& .MuiTableCell-head": {
                      color: "#1e293b",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      py: 1.5,
                      borderBottom: "2px solid #cbd5e1",
                    },
                  }}
                >
                  <TableCell align="left" sx={{ width: "30%" }}>
                    {t("Location.CodeGeneration.Location") || "LOCATION"}
                  </TableCell>
                  <TableCell align="left" sx={{ width: "35%" }}>
                    {t("Location.CodeGeneration.LocationCode") || "LOCATION CODE"}
                  </TableCell>
                  <TableCell align="left" sx={{ width: "20%" }}>
                    {t("Location.CodeGeneration.CreatedDate") || "CREATED DATE"}
                  </TableCell>
                  <TableCell align="right" sx={{ width: "15%" }}>
                    {t("Location.CodeGeneration.Actions") || "ACTIONS"}
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
            <Box
              id="scrollableDiv"
              sx={{
                maxHeight: "calc(100vh - 400px)",
                overflowY: "auto",
                overflowX: "hidden",
                "&::-webkit-scrollbar": {
                  width: "10px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f5f9",
                  borderRadius: "5px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#7c3aed",
                  borderRadius: "5px",
                  border: "2px solid #f1f5f9",
                  "&:hover": {
                    background: "#6d28d9",
                  },
                },
              }}
            >
              <InfiniteScroll
                dataLength={locationCodes.length}
                next={this.loadMore}
                hasMore={hasMore}
                loader={
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#7c3aed",
                        fontSize: "0.875rem",
                        fontWeight: 500,
                      }}
                    >
                      Loading more locations...
                    </Typography>
                  </Box>
                }
                endMessage={
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      py: 2,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#94a3b8",
                        fontSize: "0.875rem",
                      }}
                    >
                      {t("Location.CodeGeneration.AllLocationsLoaded") || "You have seen it all"}
                    </Typography>
                  </Box>
                }
                scrollableTarget="scrollableDiv"
              >
                <Table size="small" sx={{ tableLayout: "fixed" }}>
                  <TableBody>
                    {locationCodes.map((code) => (
                      <TableRow
                        key={code.id}
                        sx={{
                          backgroundColor: "#f8fafc",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "#e2e8f0",
                            transform: "translateY(-1px)",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          },
                          "& td": {
                            borderColor: "#e2e8f0",
                            fontSize: "0.875rem",
                            verticalAlign: "top",
                            py: 1.5,
                          },
                        }}
                      >
                        <TableCell
                          align="left"
                          sx={{
                            width: "30%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "6px 12px",
                              borderRadius: "6px",
                              fontWeight: 600,
                              fontSize: "0.875rem",
                              backgroundColor: code.color_code || "#000000",
                              color: this.getContrastColor(code.color_code || "#000000"),
                            }}
                          >
                            {code.location || "-"}
                          </Box>
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            width: "35%",
                            fontFamily: "monospace",
                            color: "#475569",
                            fontWeight: 500,
                          }}
                        >
                          {code.location_code || "-"}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            width: "20%",
                            color: "#475569",
                          }}
                        >
                          {code.created_on
                            ? new Date(code.created_on).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell align="right" sx={{ width: "15%" }}>
                          <IconButton
                            size="small"
                            onClick={(e) => this.handleMenuOpen(e, code)}
                            sx={{
                              color: "#64748b",
                              "&:hover": {
                                backgroundColor: "rgba(124, 58, 237, 0.1)",
                                color: "#7c3aed",
                              },
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </InfiniteScroll>
            </Box>
          </Box>
        )}

        {/* Actions Menu */}
        <Menu
          id="location-actions-menu"
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={this.handleMenuClose}
          PaperProps={{
            sx: {
              maxHeight: 200,
              width: 180,
              borderRadius: "8px",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            },
          }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem
            onClick={this.handleEditOpen}
            disabled={deleting}
            sx={{
              height: "40px",
              fontSize: "0.875rem",
              color: "#475569",
              "&:hover": {
                backgroundColor: "rgba(124, 58, 237, 0.08)",
                color: "#7c3aed",
              },
            }}
          >
            <ListItemText 
              primary={t("Location.CodeGeneration.Edit") || "Edit"} 
            />
          </MenuItem>
          <MenuItem
            onClick={this.handleDelete}
            disabled={deleting}
            sx={{
              height: "40px",
              fontSize: "0.875rem",
              color: "#ef4444",
            }}
          >
            <ListItemText 
              primary={
                deleting 
                  ? t("Location.CodeGeneration.Deleting") 
                  : t("Location.CodeGeneration.Delete")
              } 
            />
          </MenuItem>
          <MenuItem
            onClick={() => this.handleGeneratePDF(false)}
            disabled={generatingPDF}
            sx={{
              height: "40px",
              fontSize: "0.875rem",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
              {generatingPDF && (
                <CircularProgress size={16} sx={{ color: "#7c3aed" }} />
              )}
              <ListItemText 
                primary={
                  generatingPDF 
                    ? t("Location.CodeGeneration.GeneratingPDF") 
                    : t("Location.CodeGeneration.GeneratePDF")
                } 
              />
            </Box>
          </MenuItem>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={this.handleDeleteDialogClose}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            {t("Location.CodeGeneration.DeleteConfirmTitle")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              {t("Location.CodeGeneration.DeleteConfirmMessage")}
              {selectedLocation && (
                <Box component="div" sx={{ mt: 1.5, p: 1, backgroundColor: "#f8fafc", borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t("Location.CodeGeneration.Location")}: {selectedLocation.location || "-"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    {t("Location.CodeGeneration.LocationCode")}: {selectedLocation.location_code || "-"}
                  </Typography>
                </Box>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.handleDeleteDialogClose}
              color="primary"
              disabled={deleting}
              sx={{ textTransform: "none" }}
            >
              {t("Location.CodeGeneration.Cancel")}
            </Button>
            <Button
              onClick={this.handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={deleting}
              sx={{ textTransform: "none" }}
            >
              {deleting ? (
                <React.Fragment>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  {t("Location.CodeGeneration.Deleting")}
                </React.Fragment>
              ) : (
                t("Location.CodeGeneration.Delete")
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={this.handleEditClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {t("Location.CodeGeneration.EditLocation") || "Edit Location"}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label={t("Location.CodeGeneration.Location") || "Location"}
                value={editForm.location}
                onChange={this.handleEditFieldChange("location")}
                fullWidth
              />
              <TextField
                label={t("Location.CodeGeneration.LocationCode") || "Location Code"}
                value={editForm.location_code}
                onChange={this.handleEditFieldChange("location_code")}
                fullWidth
              />
              <Box>
                <Typography sx={{ mb: 1, color: "#475569", fontWeight: 500 }}>
                  {t("Location.CodeGeneration.ColorCode") || "Color"}
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {colorOptions.map((opt) => (
                    <Box
                      key={opt.color}
                      onClick={() => this.handleEditColorSelect(opt.color)}
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "8px",
                        backgroundColor: opt.color,
                        border: editForm.color_code === opt.color ? "3px solid #0ea5e9" : "2px solid #e2e8f0",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                        "&:hover": { transform: "scale(1.05)" },
                      }}
                    >
                      {editForm.color_code === opt.color && (
                        <Typography sx={{ color: "#ffffff", fontWeight: 700 }}>✓</Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleEditClose} disabled={savingEdit} sx={{ textTransform: "none" }}>
              {t("Location.CodeGeneration.Cancel") || "Cancel"}
            </Button>
            <Button
              onClick={this.handleEditSave}
              variant="contained"
              disabled={savingEdit}
              startIcon={savingEdit ? <CircularProgress size={18} /> : null}
              sx={{
                textTransform: "none",
                backgroundColor: "#7c3aed",
                boxShadow: "0 2px 4px rgba(124, 58, 237, 0.2)",
                "&:hover": {
                  backgroundColor: "#6d28d9",
                  boxShadow: "0 4px 8px rgba(124, 58, 237, 0.3)",
                },
                "&:disabled": {
                  backgroundColor: "#cbd5e1",
                  color: "#94a3b8",
                  boxShadow: "none",
                },
              }}
            >
              {savingEdit ? (t("Location.CodeGeneration.Saving") || "Saving...") : (t("Location.CodeGeneration.Save") || "Save")}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Location Dialog */}
        <Dialog
          open={addLocationDialogOpen}
          onClose={this.handleAddLocationDialogClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "20px 24px",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#1e293b",
              }}
            >
              {t("BinMapping.AddLocation") || "Add Location"}
            </Typography>
            <IconButton
              onClick={this.handleAddLocationDialogClose}
              size="small"
              sx={{
                color: "#64748b",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <BinMappingGeneration
              classes={classes}
              box={selectedBox}
              onStateChange={() => {}}
              onLocationGenerated={this.handleLocationGenerated}
            />
          </DialogContent>
        </Dialog>

        {/* Generate PDF Dialog */}
        <Dialog
          open={this.state.generatePDFDialogOpen}
          onClose={this.handleGeneratePDFDialogClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: "20px 24px",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#1e293b",
              }}
            >
              {t("Location.CodeGeneration.GeneratePDF") || "Generate PDF"}
            </Typography>
            <IconButton
              onClick={this.handleGeneratePDFDialogClose}
              size="small"
              disabled={this.state.generatingPDFFromRange}
              sx={{
                color: "#64748b",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      marginBottom: "16px", 
                      color: "#475569", 
                      fontWeight: 500, 
                      fontSize: "0.875rem" 
                    }}
                  >
                    {t("Location.CodeGeneration.LetterRange")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("Location.CodeGeneration.LetterRangeStart")}
                    value={this.state.pdfRangeForm.letterRangeStart}
                    onChange={this.handlePDFRangeFormChange("letterRangeStart")}
                    inputProps={{ maxLength: 1 }}
                    disabled={this.state.generatingPDFFromRange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("Location.CodeGeneration.LetterRangeEnd")}
                    value={this.state.pdfRangeForm.letterRangeEnd}
                    onChange={this.handlePDFRangeFormChange("letterRangeEnd")}
                    inputProps={{ maxLength: 1 }}
                    disabled={this.state.generatingPDFFromRange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      marginTop: "16px", 
                      marginBottom: "16px", 
                      color: "#475569", 
                      fontWeight: 500, 
                      fontSize: "0.875rem" 
                    }}
                  >
                    {t("BinMapping.NumberRange") || "Number Range (1 or greater)"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("BinMapping.NumberRangeStart") || "Number Range Start (1 or greater)"}
                    placeholder={t("BinMapping.NumberRangeStartPlaceholder") || "e.g., 1, 10, 100"}
                    value={this.state.pdfRangeForm.numberRangeStart}
                    onChange={this.handlePDFRangeFormChange("numberRangeStart")}
                    disabled={this.state.generatingPDFFromRange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label={t("BinMapping.NumberRangeEnd") || "Number Range End (1 or greater)"}
                    placeholder={t("BinMapping.NumberRangeEndPlaceholder") || "e.g., 10, 100, 1000"}
                    value={this.state.pdfRangeForm.numberRangeEnd}
                    onChange={this.handlePDFRangeFormChange("numberRangeEnd")}
                    disabled={this.state.generatingPDFFromRange}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button
                  onClick={this.handleGeneratePDFForAll}
                  variant="contained"
                  disabled={this.state.generatingPDFFromRange}
                  startIcon={this.state.generatingPDFFromRange ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "10px 24px",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    backgroundColor: "#7c3aed",
                    boxShadow: "0 2px 4px rgba(124, 58, 237, 0.2)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#6d28d9",
                      boxShadow: "0 4px 8px rgba(124, 58, 237, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    "&:disabled": {
                      backgroundColor: "#cbd5e1",
                      color: "#94a3b8",
                      boxShadow: "none",
                      cursor: "not-allowed",
                    },
                  }}
                >
                  {this.state.generatingPDFFromRange
                    ? t("Location.CodeGeneration.GeneratingPDF")
                    : t("Location.CodeGeneration.GenerateAll")}
                </Button>
                <Button
                  onClick={this.handleGeneratePDFFromRange}
                  variant="contained"
                  disabled={
                    this.state.generatingPDFFromRange ||
                    !this.state.pdfRangeForm.letterRangeStart ||
                    !this.state.pdfRangeForm.letterRangeEnd ||
                    !this.state.pdfRangeForm.numberRangeStart ||
                    !this.state.pdfRangeForm.numberRangeEnd
                  }
                  startIcon={this.state.generatingPDFFromRange ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    padding: "10px 24px",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    backgroundColor: "#7c3aed",
                    boxShadow: "0 2px 4px rgba(124, 58, 237, 0.2)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#6d28d9",
                      boxShadow: "0 4px 8px rgba(124, 58, 237, 0.3)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": {
                      transform: "translateY(0)",
                    },
                    "&:disabled": {
                      backgroundColor: "#cbd5e1",
                      color: "#94a3b8",
                      boxShadow: "none",
                      cursor: "not-allowed",
                    },
                  }}
                >
                  {this.state.generatingPDFFromRange
                    ? t("Location.CodeGeneration.GeneratingPDF")
                    : t("Location.CodeGeneration.GeneratePDFForRange")}
                </Button>
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
}

export default withStyles(styles)(withTranslation()(BinMapping));



// WEBPACK FOOTER //
// ./src/components/bin-mapping/index.js